import { useState } from 'react';
import {
  BookOpen,
  Briefcase,
  Building2,
  Clock,
  GraduationCap,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Tabs } from '@/components/ui/Tabs';
import { activityApi } from '@/features/activity/api/activity.api';
import { USAGE_PERIODS, type UsagePeriod } from '@/features/activity/constants';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatDate, formatDuration, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { UsageFigures, UsageGroup, UsageWindow } from '@/types/usage';

const GROUPS: { key: UsageGroup; label: string; icon: LucideIcon }[] = [
  { key: 'teachers', label: 'Teachers', icon: BookOpen },
  { key: 'students', label: 'Students', icon: GraduationCap },
  { key: 'staff', label: 'Other staff', icon: Briefcase },
];

const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-400',
  'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400',
  'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400',
  'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-400',
  'bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-400',
];

function averageLabel(figures: UsageFigures): string {
  return formatDuration(Math.round(figures.avgMinutesPerDay)) ?? '—';
}

const ROLE_STYLES: Record<
  UsageGroup,
  {
    wrapper: string;
    iconWrapper: string;
    title: string;
  }
> = {
  teachers: {
    wrapper:
      'border-indigo-200/90 dark:border-indigo-900/60 bg-gradient-to-b from-indigo-50/70 via-indigo-50/30 to-white dark:from-indigo-950/30 dark:to-slate-900 shadow-[0_4px_16px_rgba(99,102,241,0.08)]',
    iconWrapper:
      'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-400 border border-indigo-200/80 dark:border-indigo-800 shadow-2xs',
    title: 'text-indigo-900 dark:text-indigo-200',
  },
  students: {
    wrapper:
      'border-sky-200/90 dark:border-sky-900/60 bg-gradient-to-b from-sky-50/70 via-sky-50/30 to-white dark:from-sky-950/30 dark:to-slate-900 shadow-[0_4px_16px_rgba(14,165,233,0.08)]',
    iconWrapper:
      'bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-400 border border-sky-200/80 dark:border-sky-800 shadow-2xs',
    title: 'text-sky-900 dark:text-sky-200',
  },
  staff: {
    wrapper:
      'border-purple-200/90 dark:border-purple-900/60 bg-gradient-to-b from-purple-50/70 via-purple-50/30 to-white dark:from-purple-950/30 dark:to-slate-900 shadow-[0_4px_16px_rgba(147,51,234,0.08)]',
    iconWrapper:
      'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-400 border border-purple-200/80 dark:border-purple-800 shadow-2xs',
    title: 'text-purple-900 dark:text-purple-200',
  },
};

export function UsagePanel() {
  const [period, setPeriod] = useState<UsagePeriod>('7');
  const days = Number(period) as UsageWindow;

  const { data, isLoading, error, refetch } = useApiQuery(
    () => activityApi.usage(days),
    [days],
  );

  const hasAnyUse =
    data !== undefined &&
    data !== null &&
    GROUPS.some(({ key }) => data.totals[key].activePeople > 0);

  return (
    <section
      className="flex flex-col gap-4 rounded-2xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
      aria-labelledby="usage-heading"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2
            id="usage-heading"
            className="text-[15px] font-bold text-slate-800 dark:text-slate-100 tracking-tight"
          >
            Time in the app
          </h2>
          <p className="mt-0.5 text-[12px] text-slate-400 dark:text-slate-500 font-medium">
            Average minutes on a day of use
            {data && ` (${formatDate(data.from)} – ${formatDate(data.to)})`}
          </p>
        </div>
        <Tabs
          options={USAGE_PERIODS}
          value={period}
          onChange={setPeriod}
          label="Usage period"
          variant="segmented"
        />
      </div>

      {isLoading && <SkeletonLines count={5} />}

      {!isLoading && error && (
        <QueryError title="Could not load time in the app" error={error} onRetry={refetch} />
      )}

      {!isLoading && !error && data && (
        <>
          {/* Top 3 Role KPI Cards */}
          <div className="grid gap-3.5 sm:grid-cols-3">
            {GROUPS.map(({ key, label, icon: Icon }) => {
              const figures = data.totals[key];
              const style = ROLE_STYLES[key];
              return (
                <div
                  key={key}
                  className={cn(
                    'flex flex-col justify-between rounded-2xl border p-3.5 sm:p-4 transition-all hover:scale-[1.01] hover:shadow-md',
                    style.wrapper,
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className={cn('text-[13px] font-bold', style.title)}>{label}</span>
                    <span className={cn('grid h-7 w-7 place-items-center rounded-lg', style.iconWrapper)}>
                      <Icon className="h-4 w-4" />
                    </span>
                  </div>
                  <p className="mt-2 text-[26px] font-black text-slate-900 dark:text-white leading-tight tabular-nums">
                    {averageLabel(figures)}
                  </p>
                  <p className="mt-1 text-[11.5px] text-slate-500 dark:text-slate-400 font-medium">
                    per day · {formatNumber(figures.activePeople)} active ·{' '}
                    {formatDuration(figures.totalMinutes) ?? '0 mins'} in all
                  </p>
                </div>
              );
            })}
          </div>

          {/* Institute Breakdown Table */}
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-rose-500" />
                <h3 className="text-[12.5px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  Institute usage
                </h3>
              </div>
              <Link
                to="/platform/institutes"
                className="text-[12.5px] font-bold text-[#5b52f9] hover:underline"
              >
                View all
              </Link>
            </div>

            {hasAnyUse || data.institutes.length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                <table className="w-full text-left text-[12.5px]">
                  <thead className="bg-slate-50/90 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                    <tr>
                      <th className="px-3.5 py-2">Institute</th>
                      <th className="px-3.5 py-2 text-right">Teachers</th>
                      <th className="px-3.5 py-2 text-right">Students</th>
                      <th className="px-3.5 py-2 text-right">Other staff</th>
                      <th className="px-3.5 py-2 text-right">Total time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {data.institutes.map((row, idx) => {
                      const avatarColor = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                      return (
                        <tr
                          key={row.uuid}
                          className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors"
                        >
                          <td className="px-3.5 py-2">
                            <div className="flex items-center gap-2.5">
                              <span
                                className={cn(
                                  'grid h-6.5 w-6.5 shrink-0 place-items-center rounded-md text-xs font-bold',
                                  avatarColor,
                                )}
                              >
                                <Building2 className="h-3.5 w-3.5" />
                              </span>
                              <div>
                                <Link
                                  to={`/platform/institutes/${row.uuid}`}
                                  className="font-bold text-slate-800 dark:text-slate-200 hover:text-[#5b52f9]"
                                >
                                  {row.displayName}
                                </Link>
                                <p className="text-[10.5px] text-slate-400 dark:text-slate-500 font-medium">
                                  {row.code}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-3.5 py-2 text-right font-medium text-slate-700 dark:text-slate-300 tabular-nums">
                            {averageLabel(row.teachers)}
                          </td>
                          <td className="px-3.5 py-2 text-right font-medium text-slate-700 dark:text-slate-300 tabular-nums">
                            {averageLabel(row.students)}
                          </td>
                          <td className="px-3.5 py-2 text-right font-medium text-slate-700 dark:text-slate-300 tabular-nums">
                            {averageLabel(row.staff)}
                          </td>
                          <td className="px-3.5 py-2 text-right font-black text-slate-900 dark:text-white tabular-nums">
                            {row.totalMinutes === 0 ? 'No use' : formatDuration(row.totalMinutes)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                icon={<Clock className="h-6 w-6" />}
                title="No time recorded yet"
                description="Time is counted from now on, as teachers, students and staff use the web app."
              />
            )}
          </div>
        </>
      )}
    </section>
  );
}
